import type { Random } from '../../core/Random';

/**
 * Reward bands for the legacy single-chest mini-game. The original chest
 * was "always wins" — F2P casino convention is anticipation-without-loss;
 * variance came from the magnitude band rather than a fail outcome. Coin
 * range is inclusive on both ends.
 *
 * <p>{@link rollChestReward} is retained for any consumer still rolling a
 * single-chest reward. The new 3-pick mechanic uses
 * {@link rollChestPickRound} instead.
 */
export interface ChestRewardBand {
  id: 'small' | 'medium' | 'large';
  weight: number;
  coinMin: number;
  coinMax: number;
  gems: number;
}

export const CHEST_REWARD_BANDS: readonly ChestRewardBand[] = [
  { id: 'small', weight: 0.5, coinMin: 100, coinMax: 200, gems: 1 },
  { id: 'medium', weight: 0.35, coinMin: 200, coinMax: 350, gems: 2 },
  { id: 'large', weight: 0.15, coinMin: 350, coinMax: 500, gems: 3 },
];

export interface ChestReward {
  coins: number;
  gems: number;
  band: ChestRewardBand['id'];
}

/** Roll a chest reward. Picks a band by weight, then a coin amount uniform
 * within the band's [coinMin, coinMax] range. */
export function rollChestReward(random: Random): ChestReward {
  const r = random.nextFloat();
  let acc = 0;
  for (const band of CHEST_REWARD_BANDS) {
    acc += band.weight;
    if (r < acc) {
      return {
        band: band.id,
        gems: band.gems,
        coins:
          band.coinMin === band.coinMax
            ? band.coinMin
            : random.nextIntBetween(band.coinMin, band.coinMax + 1),
      };
    }
  }
  // Fallback — should be unreachable when weights sum to 1.
  const last = CHEST_REWARD_BANDS[CHEST_REWARD_BANDS.length - 1]!;
  return { band: last.id, gems: last.gems, coins: last.coinMin };
}

/* ─────────────────── 3-chest pick mechanic (current Hidden Treasure) ──── */

/**
 * Outcome of a single chest in the 3-pick round.
 *  - {@code dud}   — no reward; the "almost!" beat that gives the win its
 *    weight.
 *  - {@code small} — modest coin trickle, maybe a single gem.
 *  - {@code big}   — the headline win: chunky coins + a handful of gems.
 */
export type ChestPickOutcome = 'dud' | 'small' | 'big';

export interface ChestPickReward {
  outcome: ChestPickOutcome;
  coins: number;
  gems: number;
}

/** Coin range for the {@code small} chest in the 3-pick round (inclusive). */
export const CHEST_PICK_SMALL_COINS: readonly [number, number] = [50, 150];
/** Gem range for the {@code small} chest (inclusive). */
export const CHEST_PICK_SMALL_GEMS: readonly [number, number] = [0, 1];
/** Coin range for the {@code big} chest (inclusive). */
export const CHEST_PICK_BIG_COINS: readonly [number, number] = [250, 500];
/** Gem range for the {@code big} chest (inclusive). */
export const CHEST_PICK_BIG_GEMS: readonly [number, number] = [1, 3];

/**
 * Roll one round of the 3-chest pick mini-game. Always returns a triple
 * containing exactly one DUD, one SMALL, and one BIG reward — pre-shuffled
 * so the host scene can render them positionally without further randomisation.
 *
 * <p>The dud outcome is what makes the pick feel like a real choice: with
 * three "always wins" chests, the only thing varying is the magnitude
 * label. With one dud in the mix, the player's tap actually decides
 * something.
 */
export function rollChestPickRound(
  random: Random,
): readonly [ChestPickReward, ChestPickReward, ChestPickReward] {
  const dud: ChestPickReward = { outcome: 'dud', coins: 0, gems: 0 };
  const small: ChestPickReward = {
    outcome: 'small',
    coins: rollInclusive(random, CHEST_PICK_SMALL_COINS),
    gems: rollInclusive(random, CHEST_PICK_SMALL_GEMS),
  };
  const big: ChestPickReward = {
    outcome: 'big',
    coins: rollInclusive(random, CHEST_PICK_BIG_COINS),
    gems: rollInclusive(random, CHEST_PICK_BIG_GEMS),
  };
  return shuffle3(random, dud, small, big);
}

function rollInclusive(
  random: Random,
  range: readonly [number, number],
): number {
  const [min, max] = range;
  if (min === max) return min;
  return random.nextIntBetween(min, max + 1);
}

/** Fisher–Yates over a fixed 3-tuple. Inlined to keep the return shape
 * statically known as a 3-tuple (rather than a {@code ChestPickReward[]}). */
function shuffle3(
  random: Random,
  a: ChestPickReward,
  b: ChestPickReward,
  c: ChestPickReward,
): readonly [ChestPickReward, ChestPickReward, ChestPickReward] {
  const arr: [ChestPickReward, ChestPickReward, ChestPickReward] = [a, b, c];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = random.nextIntBetween(0, i + 1);
    const tmp = arr[i] as ChestPickReward;
    arr[i] = arr[j] as ChestPickReward;
    arr[j] = tmp;
  }
  return arr;
}
